import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';
import { normalizeSectionId, sectionRenderer } from '@theme/section-renderer';
import { isMobileBreakpoint } from '@theme/utilities';

const CART_DRAWER_REOPEN_KEY = 'cart-drawer-reopen-at';
let pageUnloading = false;
let cartSyncTimer = 0;
let cartSyncAttempts = 0;

window.addEventListener('pagehide', () => {
  pageUnloading = true;
});

/**
 * Visible pieces: quantity, excluding packaging and hidden product types.
 * @param {HTMLElement} inner
 * @param {{ items?: Array<{ handle?: string, variant_id?: number, product_type?: string, quantity?: number }> }} cart
 */
function visiblePieceCount(inner, cart) {
  const hiddenTypes = (inner.dataset.cartHiddenTypes || '')
    .split(',')
    .map((type) => type.trim().toLowerCase())
    .filter(Boolean);
  const packagingHandle = inner.dataset.packagingHandle || '';
  const packagingVariant = inner.dataset.packagingVariant || '';

  return (cart.items || []).reduce((sum, item) => {
    if (packagingVariant && String(item.variant_id) === packagingVariant) return sum;
    if (packagingHandle && item.handle === packagingHandle) return sum;
    const type = String(item.product_type || '').trim().toLowerCase();
    if (type && hiddenTypes.includes(type)) return sum;
    return sum + Number(item.quantity || 0);
  }, 0);
}

/**
 * @param {CartDrawerComponent} drawer
 */
function scheduleCartDrawerSync(drawer) {
  window.clearTimeout(cartSyncTimer);
  cartSyncTimer = window.setTimeout(() => {
    syncCartDrawer(drawer);
  }, 150);
}

/**
 * Re-render the header section when the open drawer markup does not match /cart.js.
 * Hot reload can swap in an earlier section response; cart updates render the live cart.
 * @param {CartDrawerComponent} drawer
 */
async function syncCartDrawer(drawer) {
  if (!drawer.isConnected) return;
  const inner = drawer.querySelector('[data-hydration-key="cart-drawer-inner"]');
  const section = drawer.closest('[id^="shopify-section-"]');
  if (!(inner instanceof HTMLElement) || !(section instanceof HTMLElement)) return;

  let cart;
  try {
    const cartUrl = `${Theme?.routes?.cart_url || '/cart'}.js`;
    const response = await fetch(cartUrl, { headers: { Accept: 'application/json' } });
    if (!response.ok) return;
    cart = await response.json();
  } catch {
    return;
  }

  if (!drawer.isConnected) return;
  const visibleCount = visiblePieceCount(inner, cart);
  const renderedCount = Number(inner.dataset.cartVisibleCount || 0);
  const renderedTotal = Number(inner.dataset.cartTotal || 0);
  const liveTotal = Number(cart.total_price || 0);
  if (renderedCount === visibleCount && renderedTotal === liveTotal) {
    cartSyncAttempts = 0;
    return;
  }

  if (cartSyncAttempts >= 2) return;
  cartSyncAttempts += 1;

  const url = new URL(window.location.href);
  url.searchParams.set('cart_sync', String(Date.now()));
  try {
    await sectionRenderer.renderSection(normalizeSectionId(section.id), {
      cache: false,
      url,
      mode: 'hydration',
    });
  } catch (error) {
    console.error(error);
  }

  if (!drawer.isConnected) return;
  const refreshed = drawer.querySelector('[data-hydration-key="cart-drawer-inner"]');
  const fixed =
    refreshed instanceof HTMLElement &&
    Number(refreshed.dataset.cartVisibleCount || 0) === visibleCount &&
    Number(refreshed.dataset.cartTotal || 0) === liveTotal;
  if (fixed) {
    cartSyncAttempts = 0;
    return;
  }

  scheduleCartDrawerSync(drawer);
}

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {ResizeObserver | null} */
  #stickyResizeObserver = null;

  /** @type {MutationObserver | null} */
  #cartSyncObserver = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#startStickyMonitoring);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#stopStickyMonitoring);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener('toggle', this.#handleDetailsToggle, true);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }

    this.#restoreOpenDrawer();
    this.#watchCartMarkup();
    scheduleCartDrawerSync(this);
  }

  disconnectedCallback() {
    if (!pageUnloading && this.refs.dialog?.open) {
      sessionStorage.setItem(CART_DRAWER_REOPEN_KEY, String(Date.now()));
    }
    this.#cartSyncObserver?.disconnect();
    this.#cartSyncObserver = null;
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#startStickyMonitoring);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#stopStickyMonitoring);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener('toggle', this.#handleDetailsToggle, true);
    this.#stopStickyMonitoring();
    this.#historyAbortController?.abort();
  }

  #restoreOpenDrawer() {
    const openedAt = Number(sessionStorage.getItem(CART_DRAWER_REOPEN_KEY) || 0);
    if (!openedAt || Date.now() - openedAt > 1500) return;
    sessionStorage.removeItem(CART_DRAWER_REOPEN_KEY);
    this.showDialog();
  }

  #watchCartMarkup() {
    this.#cartSyncObserver?.disconnect();
    this.#cartSyncObserver = new MutationObserver(() => {
      scheduleCartDrawerSync(this);
    });
    this.#cartSyncObserver.observe(this, {
      subtree: true,
      attributes: true,
      attributeFilter: ['data-cart-total', 'data-cart-visible-count'],
    });
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  #handleCartUpdate = () => {
    if (!this.refs.dialog?.open) return;
    requestAnimationFrame(() => this.#updateStickyState());
  };

  /** @param {Event} event */
  #handleDetailsToggle = (event) => {
    if (!(event.target instanceof HTMLDetailsElement)) return;
    if (!this.refs.dialog?.open) return;
    requestAnimationFrame(() => this.#updateStickyState());
  };

  #startStickyMonitoring = () => {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog || typeof ResizeObserver === 'undefined') return;

    this.#stopStickyMonitoring();
    this.#stickyResizeObserver = new ResizeObserver(() => {
      this.#updateStickyState();
    });

    const summary = dialog.querySelector('.cart-drawer__summary');
    this.#stickyResizeObserver.observe(dialog);
    if (summary) this.#stickyResizeObserver.observe(summary);
  };

  #stopStickyMonitoring = () => {
    this.#stickyResizeObserver?.disconnect();
    this.#stickyResizeObserver = null;
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }

}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
